export interface Receipt {
  id: string;
  expenseId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  dataUrl: string; // Base64 encoded image
  uploadDate: string;
  userId: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

export function validateReceiptFile(file: File): { valid: boolean; error?: string } {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Only JPG, PNG, WEBP, and PDF files are allowed'
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'File size must be less than 5MB'
    };
  }

  return { valid: true };
}

export async function uploadReceipt(
  file: File,
  expenseId: string,
  userId: string
): Promise<Receipt> {
  const validation = validateReceiptFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      
      const receipt: Receipt = {
        id: `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        expenseId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        dataUrl,
        uploadDate: new Date().toISOString(),
        userId
      };

      storeReceipt(receipt);
      resolve(receipt);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

export function storeReceipt(receipt: Receipt): void {
  const receipts = getReceipts(receipt.userId);
  receipts.push(receipt);
  localStorage.setItem(`receipts_${receipt.userId}`, JSON.stringify(receipts));
}

export function getReceipts(userId: string): Receipt[] {
  const stored = localStorage.getItem(`receipts_${userId}`);
  return stored ? JSON.parse(stored) : [];
}

export function getReceiptsByExpenseId(expenseId: string, userId: string): Receipt[] {
  const receipts = getReceipts(userId);
  return receipts.filter(r => r.expenseId === expenseId);
}

export function deleteReceipt(receiptId: string, userId: string): void {
  const receipts = getReceipts(userId);
  const filtered = receipts.filter(r => r.id !== receiptId);
  localStorage.setItem(`receipts_${userId}`, JSON.stringify(filtered));
}

export function deleteReceiptsByExpenseId(expenseId: string, userId: string): void {
  const receipts = getReceipts(userId);
  const filtered = receipts.filter(r => r.expenseId !== expenseId);
  localStorage.setItem(`receipts_${userId}`, JSON.stringify(filtered));
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
